/**
 * entry-schema.ts — ฟอร์มกลางรายได้อื่น/ค่าใช้จ่าย/adjustment (CONTEXT §5.4, T063)
 * ใช้ร่วมกัน web/api: web สร้างฟอร์ม dynamic, api validate ก่อนบันทึก
 *
 * กติกา:
 * - จำนวนเงินเป็น string ที่ parse เป็น Decimal ได้ (moneyString) — ห้าม float
 * - classification=tax_only บังคับ pnl_impact=false (VAT/WHT ไม่ลดกำไรอัตโนมัติ)
 * - enum ตรงกับ DB (supabase/migrations/..._financial_entries.sql)
 */
import { z } from 'zod';
import { moneyString, dateKey, uuid, salesChannelCode } from './schemas.js';

export const entryClassification = z.enum([
  'income',
  'expense',
  'asset',
  'liability',
  'tax_only',
  'adjustment',
  'reversal',
]);
export type EntryClassification = z.infer<typeof entryClassification>;

export const vatBasis = z.enum(['none', 'exclusive', 'inclusive', 'zero_rated', 'exempt']);
export const vatClaimability = z.enum(['claimable', 'non_claimable', 'partial', 'na']);
export const whtType = z.enum(['none', 'pnd1', 'pnd3', 'pnd53', 'other']);
export const paymentMethodCode = z.enum([
  'cash',
  'bank_transfer',
  'credit_card',
  'promptpay',
  'other',
  'unpaid',
]);
export const counterpartyType = z.enum(['individual', 'juristic']);
export const allocationMethod = z.enum(['direct', 'percentage', 'revenue', 'orders', 'units']);

/** เปอร์เซ็นต์ (WHT rate ฯลฯ) 0–100 */
const ratePct = z.coerce.number().min(0).max(100);

/** บรรทัดย่อยของ entry (split) */
export const entryLineInput = z.object({
  description: z.string().trim().max(500).optional(),
  pnlCategory: z.string().trim().max(120).optional(),
  skuId: uuid.optional(),
  quantity: z.coerce.number().optional(),
  amount: moneyString,
  vatAmount: moneyString.optional(),
  whtAmount: moneyString.optional(),
});
export type EntryLineInput = z.infer<typeof entryLineInput>;

/**
 * ฟอร์มกลาง — field ส่วนใหญ่ optional และแสดงตามประเภทรายการฝั่ง UI
 * required: classification + totalAmount (ยอดรวมรายการ)
 */
export const financialEntryInput = z.object({
  classification: entryClassification,

  // ---- วันที่ (เก็บแยก ไม่เขียนทับ) ----
  documentDate: dateKey.optional(),
  eventDate: dateKey.optional(),
  accountingDate: dateKey.optional(),
  paymentDate: dateKey.optional(),
  servicePeriodStart: dateKey.optional(),
  servicePeriodEnd: dateKey.optional(),

  // ---- การผูกข้อมูล ----
  shopId: uuid.optional(),
  channelCode: salesChannelCode.optional(),
  brandId: uuid.optional(),
  skuId: uuid.optional(),
  orderId: uuid.optional(),
  campaign: z.string().trim().max(200).optional(),
  costCenter: z.string().trim().max(200).optional(),

  // ---- คู่ค้า ----
  counterpartyName: z.string().trim().max(300).optional(),
  counterpartyType: counterpartyType.optional(),
  counterpartyTaxId: z.string().trim().max(30).optional(),
  counterpartyBranch: z.string().trim().max(60).optional(),

  // ---- เอกสาร ----
  documentType: z.string().trim().max(60).optional(),
  invoiceNo: z.string().trim().max(120).optional(),
  receiptNo: z.string().trim().max(120).optional(),
  debitNoteNo: z.string().trim().max(120).optional(),
  creditNoteNo: z.string().trim().max(120).optional(),

  // ---- จำนวนเงิน ----
  quantity: z.coerce.number().optional(),
  unit: z.string().trim().max(30).optional(),
  unitPrice: moneyString.optional(),
  amountBeforeVat: moneyString.optional(),
  vatAmount: moneyString.optional(),
  whtAmount: moneyString.optional(),
  totalAmount: moneyString,
  paidAmount: moneyString.optional(),
  outstandingAmount: moneyString.optional(),

  // ---- ภาษี ----
  vatBasis: vatBasis.optional(),
  vatClaimability: vatClaimability.optional(),
  whtType: whtType.optional(),
  whtRate: ratePct.optional(),
  taxDeductible: z.boolean().optional(),
  taxPeriod: dateKey.optional(),
  taxForm: z.string().trim().max(30).optional(),
  /** ปกติ derive จาก classification; tax_only จะถูกบังคับเป็น false ฝั่ง server */
  pnlImpact: z.boolean().optional(),

  // ---- การชำระ ----
  cashBankAccount: z.string().trim().max(120).optional(),
  paymentMethod: paymentMethodCode.optional(),
  paymentRef: z.string().trim().max(120).optional(),
  currency: z
    .string()
    .trim()
    .length(3)
    .regex(/^[A-Z]{3}$/, 'สกุลเงินต้องเป็นรหัส 3 ตัวอักษร')
    .optional(),
  fxRate: z.coerce.number().positive().optional(),

  // ---- การจัดประเภท P&L ----
  pnlCategory: z.string().trim().max(120).optional(),
  allocationMethod: allocationMethod.optional(),

  // ---- reversal / เหตุผล ----
  reversalOfId: uuid.optional(),
  reason: z.string().trim().max(1000).optional(),

  // ---- บรรทัดย่อย ----
  lines: z.array(entryLineInput).max(200).optional(),
});
export type FinancialEntryInput = z.infer<typeof financialEntryInput>;

/** patch สำหรับแก้ไข — ทุก field optional; reason ใช้ audit และงวดปิด */
export const financialEntryPatch = financialEntryInput.partial();
export type FinancialEntryPatch = z.infer<typeof financialEntryPatch>;

/** ตัวกรองรายการ (หน้ารายการรายได้/ค่าใช้จ่าย — T090) */
export const entryListFilter = z.object({
  classification: entryClassification.optional(),
  channelCode: salesChannelCode.optional(),
  reviewStatus: z.enum(['ok', 'needs_review']).optional(),
  from: dateKey.optional(),
  to: dateKey.optional(),
  search: z.string().trim().max(200).optional(),
});
export type EntryListFilter = z.infer<typeof entryListFilter>;
